/**
 * ApiKeyInput — BYO OpenRouter API key input + model picker + "test connection".
 *
 * Per PLAYBOOK §4 Pattern A: the user pastes their own key, it lives in
 * localStorage under `talent-align-settings`, and it never leaves the browser
 * except to call OpenRouter directly. The help text in the component tells them so.
 */

import { useEffect, useState } from "react";
import { CheckCircle2, KeyRound, Loader2, XCircle } from "lucide-react";

import { OPENROUTER_MODELS, type OpenRouterModel, useOpenRouter } from "@/hooks/useOpenRouter";

type ConnectionState =
  | { kind: "idle" }
  | { kind: "testing" }
  | { kind: "ok"; message: string }
  | { kind: "error"; message: string };

export function ApiKeyInput() {
  const { testConnection, readSettings, writeSettings } = useOpenRouter();
  const [key, setKey] = useState("");
  const [model, setModel] = useState<OpenRouterModel>("meta-llama/llama-3.3-70b-instruct");
  const [connection, setConnection] = useState<ConnectionState>({ kind: "idle" });

  useEffect(() => {
    const s = readSettings();
    if (s.openRouterApiKey) setKey(s.openRouterApiKey);
    if (s.openRouterModel) setModel(s.openRouterModel);
  }, [readSettings]);

  function handleSaveKey(next: string) {
    setKey(next);
    writeSettings({ openRouterApiKey: next });
    setConnection({ kind: "idle" });
  }

  function handleSaveModel(next: OpenRouterModel) {
    setModel(next);
    writeSettings({ openRouterModel: next });
  }

  async function handleTest() {
    if (!key.trim()) {
      setConnection({ kind: "error", message: "Paste a key first." });
      return;
    }
    setConnection({ kind: "testing" });
    try {
      const msg = await testConnection();
      setConnection({ kind: "ok", message: msg.trim() || "Connected." });
    } catch (err) {
      setConnection({
        kind: "error",
        message: err instanceof Error ? err.message : "Unknown error",
      });
    }
  }

  return (
    <div className="ta-card p-6">
      <div className="flex items-center gap-3 mb-4">
        <KeyRound className="w-5 h-5 text-blue-400" />
        <div>
          <h3 className="font-semibold text-slate-100">OpenRouter API key</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Stored only in your browser. Never sent to any server except OpenRouter.
          </p>
        </div>
      </div>

      <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">
        API key
      </label>
      <input
        type="password"
        value={key}
        onChange={(e) => handleSaveKey(e.target.value)}
        placeholder="sk-or-v1-..."
        spellCheck={false}
        autoComplete="off"
        className="w-full rounded-lg bg-slate-900/70 border border-slate-700/60 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/40 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 font-mono"
      />

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">
            Model
          </label>
          <select
            value={model}
            onChange={(e) => handleSaveModel(e.target.value as OpenRouterModel)}
            className="w-full rounded-lg bg-slate-900/70 border border-slate-700/60 focus:border-blue-500 focus:outline-none px-3 py-2 text-sm text-slate-100"
          >
            <optgroup label="Frontier / SOTA">
              <option value="openai/gpt-5.4">{OPENROUTER_MODELS["openai/gpt-5.4"]}</option>
              <option value="anthropic/claude-opus-4.6">{OPENROUTER_MODELS["anthropic/claude-opus-4.6"]}</option>
              <option value="google/gemini-3.1-pro-preview">{OPENROUTER_MODELS["google/gemini-3.1-pro-preview"]}</option>
            </optgroup>
            <optgroup label="Strong mid-tier">
              <option value="anthropic/claude-sonnet-4.6">{OPENROUTER_MODELS["anthropic/claude-sonnet-4.6"]}</option>
              <option value="openai/gpt-5.4-mini">{OPENROUTER_MODELS["openai/gpt-5.4-mini"]}</option>
              <option value="google/gemini-3-flash-preview">{OPENROUTER_MODELS["google/gemini-3-flash-preview"]}</option>
              <option value="google/gemini-2.5-flash">{OPENROUTER_MODELS["google/gemini-2.5-flash"]}</option>
              <option value="mistralai/mistral-large-2512">{OPENROUTER_MODELS["mistralai/mistral-large-2512"]}</option>
              <option value="deepseek/deepseek-v3.2">{OPENROUTER_MODELS["deepseek/deepseek-v3.2"]}</option>
              <option value="qwen/qwen3.6-plus">{OPENROUTER_MODELS["qwen/qwen3.6-plus"]}</option>
            </optgroup>
            <optgroup label="Budget / Fast">
              <option value="meta-llama/llama-3.3-70b-instruct">{OPENROUTER_MODELS["meta-llama/llama-3.3-70b-instruct"]}</option>
              <option value="google/gemma-4-31b-it">{OPENROUTER_MODELS["google/gemma-4-31b-it"]}</option>
              <option value="google/gemma-4-26b-a4b-it">{OPENROUTER_MODELS["google/gemma-4-26b-a4b-it"]}</option>
              <option value="meta-llama/llama-3.1-8b-instruct">{OPENROUTER_MODELS["meta-llama/llama-3.1-8b-instruct"]}</option>
              <option value="mistralai/mistral-small-2603">{OPENROUTER_MODELS["mistralai/mistral-small-2603"]}</option>
            </optgroup>
          </select>
        </div>

        <div className="flex items-end">
          <button
            type="button"
            onClick={handleTest}
            disabled={connection.kind === "testing"}
            className="w-full rounded-lg bg-slate-800/70 border border-slate-700/60 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-wait px-4 py-2 text-sm font-medium text-slate-100 transition flex items-center justify-center gap-2"
          >
            {connection.kind === "testing" ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Testing...
              </>
            ) : (
              "Test connection"
            )}
          </button>
        </div>
      </div>

      {connection.kind === "ok" && (
        <div className="mt-4 flex items-start gap-2 text-sm text-emerald-400">
          <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{connection.message}</span>
        </div>
      )}
      {connection.kind === "error" && (
        <div className="mt-4 flex items-start gap-2 text-sm text-rose-400">
          <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{connection.message}</span>
        </div>
      )}

      <p className="mt-4 text-xs text-slate-500 leading-relaxed">
        Don't have a key?{" "}
        <a
          href="https://openrouter.ai/keys"
          target="_blank"
          rel="noreferrer"
          className="text-blue-400 hover:text-blue-300 underline underline-offset-2"
        >
          Get one from OpenRouter
        </a>{" "}
        — $5 minimum top-up. Access 300+ models with one key.
      </p>
    </div>
  );
}
